import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { Interval } from '@ui/constants/poll';
import { GenericTradeParams, TpSlValueState } from '@ui/features/Trade/types';
import { useDataChainId } from '@ui/hooks/datachain';
import { toReadableValue } from '@ui/lib/bignumber';
import { getTradingPortalFacet } from '@ui/services/contract/contract-instances/getTradingPortalFacet';
import type { DecreaseOrderRow } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

import {
  getIsDecreaseOrderUpdateParams,
  useUpdateTradeTpAndSlParams,
} from './useUpdateTradeTpAndSlParams';

export const useEstTradeEditTpSlGasLimit = <TSelect = BigInt>({
  genericTradeParams,
  activeDecreaseOrders,
  tp,
  sl,
  opts,
}: {
  tp: TpSlValueState;
  sl: TpSlValueState;
  activeDecreaseOrders?: DecreaseOrderRow[];
  opts?: Pick<UseQueryOptions<BigInt, Error, TSelect>, 'enabled'>;
} & GenericTradeParams) => {
  const { address } = useAccount();
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });

  const updateTradeTpAndSlParams = useUpdateTradeTpAndSlParams({
    genericTradeParams,
    activeDecreaseOrders,
    tp,
    sl,
  });
  const TradingPortalFacet = useMemo(() => {
    if (!client) return;

    return getTradingPortalFacet({
      chainId,
      publicClient: client,
    });
  }, [chainId, client]);

  return useQuery({
    meta: {
      type: 'estimateGas',
    },
    queryKey: [
      'TradingPortalFacet.estimate.updateTradeTpSl',
      address,
      chainId,
      client?.uid,
      activeDecreaseOrders,
      updateTradeTpAndSlParams,
    ],
    queryFn: async () => {
      if (isNil(updateTradeTpAndSlParams) || isNil(address)) return undefined;

      if (!getIsDecreaseOrderUpdateParams(updateTradeTpAndSlParams)) {
        const re = await TradingPortalFacet?.estimateGas.updateTradeTpAndSl(
          [...updateTradeTpAndSlParams],
          {
            account: address,
          },
        );

        if (isNil(re)) return undefined;

        return toReadableValue(re);
      }

      const gasResults = await Promise.all([
        ...(updateTradeTpAndSlParams.updateOrders.length > 0
          ? [
              TradingPortalFacet?.estimateGas.batchUpdateDecreaseOrders(
                [updateTradeTpAndSlParams.updateOrders],
                {
                  account: address,
                },
              ),
            ]
          : []),
        ...updateTradeTpAndSlParams.cancelOrderHashes.map((orderHash) =>
          TradingPortalFacet?.estimateGas.cancelDecreaseOrder([orderHash], {
            account: address,
          }),
        ),
        ...(updateTradeTpAndSlParams.createOrders.length > 0
          ? [
              TradingPortalFacet?.estimateGas.batchCreateDecreaseOrders(
                [genericTradeParams.id, updateTradeTpAndSlParams.createOrders],
                {
                  account: address,
                },
              ),
            ]
          : []),
      ]);
      const re = gasResults.reduce<bigint>(
        (acc, value) => acc + (value ?? 0n),
        0n,
      );

      if (isNil(re)) return undefined;

      return toReadableValue(re);
    },
    refetchInterval: Interval.SLOW,
    enabled:
      !!address &&
      !!updateTradeTpAndSlParams &&
      !!TradingPortalFacet &&
      (opts?.enabled ?? true),
  });
};
